import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ControlContainer, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';

import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { TranslocoService } from '@ngneat/transloco';
import { merge } from 'lodash-es';

import { Dialog } from 'primeng/dialog';
import { Dropdown } from 'primeng/dropdown';
import { Listbox } from 'primeng/listbox';
import { MultiSelect } from 'primeng/multiselect';

import { Memoize } from '@core/decorators';
import { ComboBoxColumn, ComboBoxColumnConfig, DropDownList } from '@core/interfaces';
import { remToPx } from '@util/layout';
import { randomId } from '@util/random-id';

import { columnsDefaultConfig } from './combo-box.columns';

@Component({
  selector: 'z-combo-box',
  templateUrl: './combo-box.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class ComboBoxComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<boolean>();

  @Input() list: DropDownList;

  @Input() fieldName: string;

  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() uppercase: boolean = false;

  @Input() popupList: boolean = false;
  @Input() clearButton: boolean = true;

  @Input() label: string;
  @Input() hint: string;
  @Input() placeholder: string;
  @Input() editable: boolean = false;

  @Input() displayValidationErrors: boolean = true;

  @Input() hideLabel: boolean = false;

  @Input() styleClass: string = null;
  @Input() virtualScroll: boolean = false;

  // selected item
  @Input() selectedItemShowDescription: boolean = true;
  @Input() selectedItemLabelColumn: string = 'column1';
  @Input() selectedItemDescriptionColumn: string = 'column2';

  // dropdown properties
  @Input() dropdownStyleClass: string = null;
  @Input() dropdownAllowMultipleValues: boolean = false;

  @Input() dropdownPanelMinHeight: number = 60;
  @Input() dropdownPanelMaxHeight: number = 400;
  @Input() dropdownItemSize: number = 32;

  @Input() dropdownPanelMinWidth: number;
  @Input() dropdownFilter: boolean = false;
  @Input() dropdownFilterBy: string = null;

  @Input() dropdownOptionValueColumn: string = 'column1';
  @Input() dropdownOptionLabelColumn: string = 'column2';

  // multiSelect list properties
  @Input() showToggleAll: boolean = true;
  @Input() selectionLimit: number = null;

  // popup list properties
  @Input() popupOnly: boolean = false;
  @Input() popupAllowMultipleValues: boolean = false;
  @Input() doubleClickToOpenPopup: boolean = true;

  @Input() popupOptionValueColumn: string = 'column1';
  @Input() popupOptionLabelColumn: string = 'column2';

  @Input() popupWindowIcon: string = 'fad fa-list';
  @Input() popupWindowTitle?: string;

  @Input() popupStyleClass: string = 'dialog-medium';
  @Input() popupFilter: boolean = true;

  @Input() selectButtonLabel: string;
  @Input() selectButtonIcon: string = 'pi pi-check';
  @Input() selectButtonClass: string = 'p-button-primary';

  @Input() cancelButtonLabel: string;
  @Input() cancelButtonIcon: string;
  @Input() cancelButtonClass: string = 'p-button-secondary';

  @Input() columnsConfig: ComboBoxColumnConfig = {};

  @Input() loading: boolean = false;

  @Output() change = new EventEmitter<string>();
  @Output() click = new EventEmitter<any>();
  @Output() focus = new EventEmitter<any>();
  @Output() blur = new EventEmitter<any>();
  @Output() dropDownPanelShow = new EventEmitter<any>();
  @Output() dropDownPanelHide = new EventEmitter<any>();
  @Output() multiSelectPanelShow = new EventEmitter<any>();
  @Output() multiSelectPanelHide = new EventEmitter<any>();
  @Output() clear = new EventEmitter<any>();
  @Output() filter = new EventEmitter<any>();
  @Output() filterDropdown = new EventEmitter<any>();
  @Output() filterMultiSelect = new EventEmitter<any>();
  @Output() dialogShow = new EventEmitter<any>();
  @Output() dialogHide = new EventEmitter<any>();
  @Output() dialogResizeInit = new EventEmitter<any>();
  @Output() dialogResizeEnd = new EventEmitter<any>();
  @Output() dialogDragEnd = new EventEmitter<any>();
  @Output() dialogMaximize = new EventEmitter<any>();
  @Output() listboxChange = new EventEmitter<any>();
  @Output() listboxClick = new EventEmitter<any>();
  @Output() listboxDblClick = new EventEmitter<any>();

  @ViewChild('listbox') listbox: Listbox;
  @ViewChild('dropdown') dropdown: Dropdown;
  @ViewChild('multiSelect') multiSelect: MultiSelect;
  @ViewChild('input') input: ElementRef<HTMLInputElement>;
  @ViewChild('dialog') dialog: Dialog;

  id: string;
  blockId: string;

  display$ = new BehaviorSubject<boolean>(false);

  hasValue$ = new BehaviorSubject<boolean>(false);

  dropdownPanelHeight$ = new BehaviorSubject<string>(`${this.dropdownPanelMaxHeight}px`);

  dropdownColumnWidthClass: string[] = [];
  popupColumnWidthClass: string[] = [];
  dropdownPanelMinWidthStyle: any = {};

  tooltipValue$ = new Subject<any>();

  form: FormGroup;

  translate = (key: string | string[], params?: Record<string, string>, lang?: string) =>
    this.translocoService.translate(key, params, lang);

  listHeight$ = new BehaviorSubject<any>({ height: '34rem' });

  dialogClass: string;

  container: HTMLDivElement;
  containerResizeObserver: ResizeObserver;

  constructor(private translocoService: TranslocoService, public formGroup: FormGroupDirective, private ngZone: NgZone) {}

  ngOnInit() {
    this.columnsConfig = merge({}, columnsDefaultConfig, this.columnsConfig);

    this.form = new FormGroup({
      value: new FormControl(),
    });

    this.translocoService
      .selectTranslation()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.selectButtonLabel = this.selectButtonLabel ?? this.translate('core.components.combo_box.select');
        this.cancelButtonLabel = this.cancelButtonLabel ?? this.translate('core.components.combo_box.cancel');
      });

    this.id = `z-combo-box-${this.fieldName}`;
    this.blockId = `${this.id}-${randomId()}`;

    this.dialogClass = `z-combo-box-dialog-${randomId()}`;
    this.popupStyleClass = `${this.dialogClass} ${this.popupStyleClass}`;

    if (this.popupOnly) {
      this.popupList = true;
    }

    if (!this.popupWindowTitle) {
      this.popupWindowTitle = this.label;
    }

    if (this.list?.length > 100) {
      this.virtualScroll = true;
    }

    // starting from 1, not 0 to match how columns are configured
    for (let i = 1; i <= 10; i++) {
      const col = `column${i}`;

      // if column 3 or greater is displayed, give column 2 a width if it doesn't already have one to prevent issues
      if (i >= 3) {
        if (this.columnsConfig[col].dropdown.display && this.columnsConfig.column2.dropdown.width === 0) {
          this.columnsConfig.column2.dropdown.width = 200;
        }

        if (this.columnsConfig[col].popup.display && this.columnsConfig.column2.popup.width === 0) {
          this.columnsConfig.column2.popup.width = 200;
        }
      }

      // set classes based on column width
      const dropdownWidth: number = this.columnsConfig[col].dropdown.width;
      const popupWidth: number = this.columnsConfig[col].popup.width;

      this.columnsConfig[col].dropdown.class = `fixed-column-${dropdownWidth}`;
      this.columnsConfig[col].popup.class = `fixed-column-${popupWidth}`;
    }

    this.dropdownPanelMinWidthStyle = this.dropdownPanelMinWidth ? { minWidth: `${this.dropdownPanelMinWidth}px` } : {};

    // attach double click handler to form element
    if (this.popupList && this.doubleClickToOpenPopup) {
      setTimeout(() => {
        if (this.dropdown) {
          this.dropdown.containerViewChild.nativeElement.addEventListener('dblclick', (event: any) => {
            this.dblClickHandler(event);
          });
        }

        if (this.multiSelect) {
          this.multiSelect.containerViewChild.nativeElement.addEventListener('dblclick', (event: any) => {
            this.dblClickHandler(event);
          });
        }

        if (this.input) {
          this.input.nativeElement.addEventListener('dblclick', (event: any) => {
            this.dblClickHandler(event);
          });
        }
      }, 0);
    }

    if (this.formGroup.form.get(this.fieldName)) {
      if (!this.formGroup.form.get(this.fieldName).value) {
        this.hasValue$.next(false);
      } else {
        this.hasValue$.next(true);
      }

      this.formGroup.form
        .get(this.fieldName)
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((value) => {
          this.onChange({ value });

          if (!value) {
            this.hasValue$.next(false);
          } else {
            this.hasValue$.next(true);
          }

          this.form.get('value').setValue(value);

          if (typeof value === 'string' || !value) {
            this.tooltipValue$.next(value);
          } else {
            this.tooltipValue$.next(value.join(', '));
          }
        });

      this.formGroup.form
        .get(this.fieldName)
        .statusChanges.pipe(takeUntil(this.destroy$))
        .subscribe((status) => {
          if (status === 'DISABLED') {
            this.disabled = true;
          } else {
            this.disabled = false;
          }
        });
    }
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();

    this.destroyResizeObserver();
  }

  ngAfterViewInit() {
    if (this.multiSelect && this.multiSelect.value && typeof this.multiSelect.value === 'string') {
      this.multiSelect.value = (this.multiSelect.value as string).split(',');
    }

    this.initResizeObserver();
  }

  openPopupList() {
    if (this.readonly) {
      return;
    }

    this.display$.next(true);
    this.calculatePanelSize();
  }

  isInvalid() {
    if (!this.formGroup.form.get(this.fieldName)) {
      return false;
    }

    return !this.formGroup.form.get(this.fieldName).pristine && this.formGroup.form.get(this.fieldName).invalid;
  }

  dblClickHandler = (_event: any) => {
    this.openPopupList();
  };

  selectValue = () => {
    if (this.form.get('value').value) {
      const value = this.form.get('value').value;

      this.formGroup.form.get(this.fieldName).setValue(value);
      this.change.emit(value);
    }

    this.closePopupList();
  };

  closePopupList = () => {
    if (this.list?.length > 0) {
      this.listbox.uncheckAll();
    }

    this.display$.next(false);
  };

  @Memoize()
  conditionalDropdownStyleClass(column: ComboBoxColumn, value: string) {
    if (column?.dropdown?.conditionalStyleClass && column?.dropdown?.conditionalStyleClass?.length > 0) {
      const styleClass: string[] = [];

      for (const condition of column.dropdown.conditionalStyleClass) {
        if (condition.value === value) {
          styleClass.push(condition.styleClass);
        }
      }

      if (styleClass.length > 0) {
        return styleClass.join(' ');
      } else {
        return null;
      }
    }

    return null;
  }

  calculateDropdownPanelSize() {
    let calculatedHeight: number;

    if (this.popupOnly) {
      return;
    }

    if (this.multiSelect && this.dropdownAllowMultipleValues && this.multiSelect.optionsToRender) {
      calculatedHeight = this.multiSelect.optionsToRender.length * this.dropdownItemSize;
    } else if (this.dropdown && !this.dropdownAllowMultipleValues && this.dropdown.optionsToDisplay) {
      calculatedHeight = this.dropdown.optionsToDisplay.length * this.dropdownItemSize;
    } else {
      return;
    }

    if (calculatedHeight < this.dropdownPanelMaxHeight) {
      if (calculatedHeight > this.dropdownPanelMinHeight) {
        this.dropdownPanelHeight$.next(`${calculatedHeight + 16}px`);
      } else {
        this.dropdownPanelHeight$.next(`${this.dropdownPanelMinHeight}px`);
      }
    } else {
      this.dropdownPanelHeight$.next(`${this.dropdownPanelMaxHeight}px`);
    }
  }

  // event handlers
  onChange = (event: any) => {
    this.change.emit(event.value);
  };

  onClick = (event: any) => {
    this.click.emit(event);
  };

  onFocus = (event: any) => {
    this.focus.emit(event);
  };

  onBlur = (event: any) => {
    this.blur.emit(event);
  };

  onDropDownPanelShow = (event: any) => {
    this.dropDownPanelShow.emit(event);

    this.calculateDropdownPanelSize();
  };

  onDropDownPanelHide = (event: any) => {
    this.dropDownPanelHide.emit(event);
  };

  onClear = (event: any) => {
    this.clear.emit(event);
  };

  onMultiSelectClear = (event: any) => {
    this.formGroup.form.get(this.fieldName).setValue('');
    this.clear.emit(event);
  };

  onFilter = (event: any) => {
    this.filter.emit(event);

    this.calculateDropdownPanelSize();
  };

  onFilterDropdown = (event: any) => {
    this.filterDropdown.emit(event);
    this.onFilter(event);
  };

  onFilterMultiSelect = (event: any) => {
    this.filterMultiSelect.emit(event);
    this.onFilter(event);
  };

  onMultiSelectPanelShow = (event: any) => {
    this.multiSelectPanelShow.emit(event);

    this.calculateDropdownPanelSize();
  };

  onMultiSelectPanelHide = (event: any) => {
    this.multiSelectPanelHide.emit(event);
  };

  onDialogShow = (event: any) => {
    this.dialogShow.emit(event);
  };

  onDialogHide = (event: any) => {
    this.dialogHide.emit(event);
  };

  onDialogResizeInit = (event: any) => {
    this.dialogResizeInit.emit(event);
  };

  onDialogResizeEnd = (event: any) => {
    this.dialogResizeEnd.emit(event);
  };

  onDialogDragEnd = (event: any) => {
    this.dialogDragEnd.emit(event);
  };

  onDialogMaximize = (event: any) => {
    this.dialogMaximize.emit(event);
  };

  onListboxChange = (event: any) => {
    this.listboxChange.emit(event);
  };

  onListboxClick = (event: any) => {
    this.listboxClick.emit(event);
  };

  onListboxDblClick = (event: any) => {
    this.listboxDblClick.emit(event);
    this.selectValue();
  };

  calculatePanelSize() {
    if (!this.popupList) {
      return;
    }

    setTimeout(() => {
      if (!this.container) {
        this.initResizeObserver();
      }

      const header: HTMLDivElement = this.container.querySelector('.p-dialog-header');
      const footer: HTMLDivElement = this.container.querySelector('.p-dialog-footer');
      const listHeader: HTMLDivElement = this.container.querySelector('.p-listbox-header');

      let offset = 0;

      if (header) {
        offset += Math.floor(header.offsetHeight);
      }

      if (footer) {
        offset += Math.floor(footer.offsetHeight);
      }

      if (listHeader) {
        offset += Math.floor(listHeader.offsetHeight);
      }

      offset += remToPx('2rem');

      this.listHeight$.next({ height: `${Math.floor(this.container.offsetHeight) - offset}px` });
    }, 0);
  }

  initResizeObserver() {
    if (!this.popupList) {
      return;
    }

    this.containerResizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        this.calculatePanelSize();
      });
    });

    try {
      this.container = document.querySelector(`.${this.dialogClass} .p-dialog-content`);

      if (this.container) {
        this.containerResizeObserver.observe(this.container);
      }
    } catch {}
  }

  destroyResizeObserver() {
    if (!this.popupList) {
      return;
    }

    try {
      if (this.containerResizeObserver && this.container) {
        this.containerResizeObserver.unobserve(this.container);
      }
    } catch {}
  }
}
